import { IFAutoComplete } from '@/components/form/f-auto-complete';
import { IFDatePickerProps } from '@/components/form/f-date-picker';
import { IFSelect } from '@/components/form/f-select-input';
import { IFTextInput } from '@/components/form/f-text-input';
import { IFTextAreaInput } from '@/components/form/f-textarea';
import { FormikValues, PageTypeEnum, TableTypes } from '@/constant';
import { TGetListUserSettingResponse } from '@/services';
import React, { ReactNode, RefObject } from 'react';

import { Table } from 'antd';
import { TableProps as InternalTableProps } from 'antd/es/table/InternalTable';

import { AnyObject, AnyObjectSchema } from 'yup';

import { ISearchFilterRef } from '../search-filter/type';
import { TActions } from './components/actions';
import { EditableRowRef } from './components/editable';

export type TableWithActionProps = TableProps &
	TActions & {
		onPageChange?: (page: number, pageSize: number) => void;
		containerClassName?: string;
		HeaderComponent?: React.ReactNode;
	};

export interface TableProps<RecordType = any>
	extends Omit<InternalTableProps, 'expandable'> {
	editable?: boolean;
	resizable?: boolean;
	allowSelectCheckboxOnRow?: boolean;
	validationSchema?:
		| AnyObjectSchema
		| ((settingFiledList: TGetListUserSettingResponse) => AnyObjectSchema);
	handleOnRow?: (record: AnyObject) => void;
	tableType?: TableTypes;
	toDetailPathName?: string;
	searchFilterRef?: RefObject<ISearchFilterRef>;
	editableRowRefs?: RefObject<{
		[key: string]: RefObject<EditableRowRef>;
	}>;
	rowSelection?: InternalTableProps['rowSelection'] & {
		selectedRows?: RecordType[];
		onReset?: () => void;
		setState?: (
			newState: Partial<{
				selectedRowKeys: React.Key[];
				selectedRows: RecordType[];
			}>
		) => void;
	};
	columns?: ColumnTypes;
	idKeyToDetail?: string;
	isSearching?: boolean;
	mergeHeader?: boolean;
	settingListFields?: TGetListUserSettingResponse;
	needToReloadListAfterClosePopup?: boolean;
	callbackAfterClosePopup?: (data?: any) => void;
	showAllSorter?: boolean;
	toDetailQuery?: (record: AnyObject) => any;
	// For expandable table
	expandable?: boolean;
}

type EditableTableProps = Parameters<typeof Table>[0];

export type ColumnTypes = (Exclude<
	EditableTableProps['columns'],
	undefined
>[number] &
	Omit<EditableCellBaseProps, 'editable'> & {
		objectName?: PageTypeEnum;
		children?: ColumnTypes;
		required?: boolean;
		dateProps?: DateProps | ((record: FormikValues) => DateProps);
		editable?: ((record: FormikValues) => boolean) | boolean;
		exportTitle?: string;
		minWidth?: number;
		sortKey?: string;
	})[];

export interface DataType {
	key: React.Key;
	franchiseName: string;
	applicantName: string;
	age: number;
	address: string;
	phone: string;
	postalCode: string;
}

export enum EditableCellType {
	TEXT_INPUT = 'TEXT_INPUT',
	CURRENCY = 'CURRENCY',
	PHONE_NUMBER = 'PHONE_NUMBER',
	DROPDOWN = 'DROPDOWN',
	AUTOCOMPLETE = 'AUTOCOMPLETE',
	DATE = 'DATE',
	DATERANGE = 'DATERANGE',
	TIME = 'TIME',
	TIMERANGE = 'TIMERANGE',
	TEXTAREA = 'TEXTAREA',
	DROPDOWN_INPUT = 'DROPDOWN_INPUT',
}

type DateProps = Partial<IFDatePickerProps> & {
	valueKey?: string;
	formatToUpdate?: string;
	startDateKey?: string;
	endDateKey?: string;
	startTimeKey?: string;
	endTimeKey?: string;
	startSameEndDate?: boolean;
	ignoreSubmitKeys?: string[];
};

export interface EditableCellBaseProps {
	editable?: boolean;
	dataIndex: string;
	keyUpdate?: string;
	cellType?: EditableCellType;
	inputProps?: Partial<IFTextInput> & {
		disableByRecord?: (record: FormikValues) => IFTextInput['disabled'];
		ignoreSubmitKeys?: string[];
	};
	textAreaProps?: Partial<IFTextAreaInput>;
	dropdownProps?: Partial<IFSelect> & {
		optionsByRecord?: (record: FormikValues) => IFSelect['options'];
		disableByRecord?: (record: FormikValues) => IFSelect['disabled'];
		getAdditionalValues?: (value: any, prevValues: any) => Partial<any> | void;
		getAdditionalValuesOnError?: (
			value: any,
			prevValues: any,
			initialValues?: FormikValues
		) => Partial<any> | void;
		ignoreSubmitKeys?: string[];

		// For DROPDOWN_INPUT
		otherToken?: string;
	};
	dateProps?: DateProps;
	autoCompleteProps?: Partial<IFAutoComplete>;
	renderCell?(cellProps: any): React.ReactNode;
	fieldsToClose?: string[];
	removeKeys?: string[];
}

export type EditableRowProps<T extends TableTypes> = {
	index: number;
	validationSchema: any | (() => any);
	children: any[];
	tableType?: T;
	needToReloadListAfterClosePopup?: boolean;
};

export interface EditableCellProps extends EditableCellBaseProps {
	title: React.ReactNode | string;
	children: React.ReactNode | any;
	record: FormikValues;
	handleSave: (
		record: Partial<FormikValues>,
		callback?: () => void,
		submit?: (newData?: FormikValues) => void,
		resetValue?: (closeEdit?: boolean) => void
	) => void;
	handleError?: (
		record: Partial<FormikValues>,
		errors?: Partial<FormikValues>,
		callback?: () => void
	) => void;
	callbackSuccess?: (record: Partial<FormikValues>) => void;
	renderCell?(cellProps: any): React.ReactNode;
	objectName?: PageTypeEnum;
}

export interface ResizableCellProps {
	onResize?: (width: number) => any;
	width: number;
	children: ReactNode;
	className?: string;
}
